import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  FormControl,
  Select,
  MenuItem,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";
import {
  loadDashboardAttendantVisibility,
  saveDashboardAttendantVisibility,
} from "../../utils/dashboardAttendantVisibility";

const useStyles = makeStyles((theme) => ({
  hint: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  table: {
    minWidth: 420,
  },
}));

const MODES = {
  NORMAL: "normal",
  HIDDEN: "hidden",
  COVERAGE: "coverage",
};

const DashboardAttendantVisibilityDialog = ({
  open,
  onClose,
  companyId,
  userId,
  users,
  onSaved,
}) => {
  const classes = useStyles();
  const [modesById, setModesById] = useState({});

  const sortedUsers = useMemo(() => {
    if (!Array.isArray(users)) return [];
    return [...users].sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""), "pt", {
        sensitivity: "base",
      })
    );
  }, [users]);

  useEffect(() => {
    if (!open || companyId == null || userId == null) return;
    const { hiddenIds, coverageIds } = loadDashboardAttendantVisibility(
      companyId,
      userId
    );
    const hidden = new Set(hiddenIds);
    const coverage = new Set(coverageIds);
    const next = {};
    sortedUsers.forEach((u) => {
      const id = Number(u.id);
      if (hidden.has(id)) next[id] = MODES.HIDDEN;
      else if (coverage.has(id)) next[id] = MODES.COVERAGE;
      else next[id] = MODES.NORMAL;
    });
    setModesById(next);
  }, [open, companyId, userId, sortedUsers]);

  const handleChange = (id, mode) => {
    setModesById((prev) => ({ ...prev, [Number(id)]: mode }));
  };

  const handleSave = () => {
    const hiddenIds = [];
    const coverageIds = [];
    Object.entries(modesById).forEach(([idStr, mode]) => {
      const id = Number(idStr);
      if (!id) return;
      if (mode === MODES.HIDDEN) hiddenIds.push(id);
      if (mode === MODES.COVERAGE) coverageIds.push(id);
    });
    saveDashboardAttendantVisibility(companyId, userId, {
      hiddenIds,
      coverageIds,
    });
    if (typeof onSaved === "function") onSaved();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {i18n.t("dashboard.attendantVisibility.title") ||
          "Visibilidade dos atendentes na dashboard"}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" className={classes.hint}>
          {i18n.t("dashboard.attendantVisibility.hint") ||
            "Estas opções ficam guardadas neste navegador (cache local) para o seu utilizador. Oculto: não aparece no ranking. Cobertura: aparece no fim da lista com o selo correspondente."}
        </Typography>
        <Table size="small" className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell>
                {i18n.t("dashboard.attendantVisibility.colName") || "Nome"}
              </TableCell>
              <TableCell align="right" width={200}>
                {i18n.t("dashboard.attendantVisibility.colMode") || "Mostrar como"}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedUsers.map((u) => {
              const id = Number(u.id);
              const mode = modesById[id] || MODES.NORMAL;
              return (
                <TableRow key={id}>
                  <TableCell>{u.name || `#${id}`}</TableCell>
                  <TableCell align="right">
                    <FormControl variant="outlined" size="small" fullWidth>
                      <Select
                        value={mode}
                        onChange={(e) => handleChange(id, e.target.value)}
                      >
                        <MenuItem value={MODES.NORMAL}>
                          {i18n.t("dashboard.attendantVisibility.modeNormal") ||
                            "Normal (ranking)"}
                        </MenuItem>
                        <MenuItem value={MODES.COVERAGE}>
                          {i18n.t("dashboard.attendantVisibility.modeCoverage") ||
                            "Cobertura (no fim)"}
                        </MenuItem>
                        <MenuItem value={MODES.HIDDEN}>
                          {i18n.t("dashboard.attendantVisibility.modeHidden") ||
                            "Oculto"}
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="default">
          {i18n.t("dashboard.attendantVisibility.cancel") || "Cancelar"}
        </Button>
        <Button onClick={handleSave} color="primary" variant="contained">
          {i18n.t("dashboard.attendantVisibility.save") || "Guardar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DashboardAttendantVisibilityDialog;
